/**
 * Parse de planilha SEM travar a interface.
 *
 * `parsearPlanilhaSemTravar(tipo, file)` le o arquivo e faz o parse pesado
 * num Web Worker (planilhaParse.worker.js). A thread principal fica livre:
 * o usuario navega, os spinners giram e os toasts aparecem — o oposto do
 * comportamento antigo, em que o XLSX.read sincrono congelava o ERP inteiro.
 *
 * Fallback deliberado: se o ambiente nao suportar Worker (navegador antigo,
 * teste sem DOM), cai no parse sincrono na propria thread — o mesmo caminho
 * que sempre existiu. Degradar para "funciona mas trava" e melhor do que
 * "nao funciona".
 *
 * O worker e singleton com desligamento por ociosidade: criar worker custa
 * (~dezenas de ms + memoria do bundle xlsx), entao reusamos entre chamadas
 * proximas (boot carrega GERENCIAL e DISTRIBUICAO em sequencia) e desligamos
 * apos 30s parado para devolver a memoria.
 */

import { criarWorkerDePlanilha } from "./planilhaWorkerFactory";

let workerAtual = null;
let desligamentoAgendado = null;
let proximoId = 1;
const pendentes = new Map();

const OCIOSIDADE_ATE_DESLIGAR_MS = 30000;

function obterWorker() {
  if (workerAtual) return workerAtual;
  workerAtual = criarWorkerDePlanilha();
  workerAtual.onmessage = (evento) => {
    const { id, ok, resultado, erro } = evento.data || {};
    const pendente = pendentes.get(id);
    if (!pendente) return;
    pendentes.delete(id);
    if (ok) {
      pendente.resolve(resultado);
    } else {
      // Erro DE PARSE (planilha invalida, aba faltando...): e resposta final,
      // nao problema de infraestrutura. Marcado para o chamador NAO tentar o
      // fallback sincrono — reprocessar um arquivo invalido na thread
      // principal so traria de volta o congelamento, para falhar igual.
      const erroParse = new Error(erro || "Falha ao processar a planilha.");
      erroParse.ehErroDeParse = true;
      pendente.reject(erroParse);
    }
    agendarDesligamento();
  };
  workerAtual.onerror = (evento) => {
    // Erro de carga do proprio worker (nao do parse): rejeita tudo que espera
    // e derruba o worker — a proxima chamada tenta recriar ou cai no fallback.
    const erro = new Error(evento?.message || "Worker de planilha falhou.");
    pendentes.forEach((p) => p.reject(erro));
    pendentes.clear();
    encerrarWorker();
  };
  return workerAtual;
}

function encerrarWorker() {
  if (desligamentoAgendado) {
    clearTimeout(desligamentoAgendado);
    desligamentoAgendado = null;
  }
  if (workerAtual) {
    workerAtual.terminate();
    workerAtual = null;
  }
}

function agendarDesligamento() {
  if (desligamentoAgendado) clearTimeout(desligamentoAgendado);
  desligamentoAgendado = setTimeout(() => {
    if (pendentes.size === 0) encerrarWorker();
  }, OCIOSIDADE_ATE_DESLIGAR_MS);
}

async function parseSincronoDeFallback(tipo, buffer, filename) {
  // Imports dinamicos para nao puxar o xlsx pro bundle principal a toa —
  // no caminho feliz ele vive so dentro do chunk do worker.
  if (tipo === "distribuicao") {
    const { processarBufferDistribuicao } = await import("./distribuicaoClient");
    return processarBufferDistribuicao(buffer, filename);
  }
  const { processarBufferCapacidade, processarBufferPlanilha } = await import("./planilhaClient");
  // "capacidade": so o extrato da Capacidade da Fabrica (importacao de copia
  // antiga), sem o parse completo que alimenta o DadosContext.
  if (tipo === "capacidade") return processarBufferCapacidade(buffer, filename);
  return processarBufferPlanilha(buffer, filename);
}

export async function parsearPlanilhaSemTravar(tipo, file) {
  if (!file) {
    throw new Error("Nenhum arquivo de planilha foi selecionado.");
  }
  const buffer = await file.arrayBuffer();

  if (typeof Worker === "undefined") {
    return parseSincronoDeFallback(tipo, buffer, file.name);
  }

  try {
    const worker = obterWorker();
    return await new Promise((resolve, reject) => {
      const id = proximoId;
      proximoId += 1;
      pendentes.set(id, { resolve, reject });
      // buffer como transferable: muda de dono sem copiar (planilhas tem MBs).
      worker.postMessage({ id, tipo, buffer, filename: file.name }, [buffer]);
    });
  } catch (erro) {
    // Planilha invalida e resposta definitiva — propaga.
    if (erro?.ehErroDeParse) throw erro;
    // Worker indisponivel (CSP, build exotico): o buffer pode ter sido
    // transferido para o worker morto, entao rele do File antes do fallback.
    const bufferNovo = await file.arrayBuffer();
    return parseSincronoDeFallback(tipo, bufferNovo, file.name);
  }
}
